type ErrorLike = {
  message?: string;
  name?: string;
  cause?: unknown;
  meta?: { reason?: string };
  info?: {
    reason?: string;
    message?: string;
    error?: { message?: string };
  };

  /**
   * ZenStack 3 `ORMError.reason`. Checked structurally rather than via `instanceof ORMError`: this
   * package is a dependency-free leaf consumed by client bundles, and the values reaching this
   * branch have crossed a serialization boundary anyway, so they have no prototype to test.
   */
  reason?: string;
};

type StructuredErrorLike = {
  message?: string;
  statusCode?: number;
  responseBody?: string;
  url?: string;
  modelId?: string;
  modelType?: string;
  text?: string;
  value?: unknown;
  context?: { field?: string; entityName?: string; entityId?: string };
  data?: unknown;
};

export const UNKNOWN_ERROR_MESSAGE = 'Unknown error';

/** ZenStack client-helpers default when the RPC response carries detail in `error.info`. */
export const ZENSTACK_GENERIC_FETCH_ERROR_MESSAGE = 'An error occurred while fetching the data.';

const GENERIC_ERROR_NAMES = new Set(['Error', 'AggregateError']);

const CHAT_STREAM_FALLBACK_MESSAGE = 'An unexpected error occurred during AI generation';

const HTTP_FETCH_FAILURE_MESSAGE = 'Failed to fetch the chat response.';

/**
 * Strips decorative quoting and braces from error messages.
 */
function sanitizeMessage(message: string): string {
  return message.replace(/^["'](.+(?=["']$))["']$/, '$1').replace(/[{}]/g, '');
}

/**
 * True when the message is non-empty and not a generic Error class name.
 */
function isInformativeMessage(message: string): boolean {
  const trimmed = message.trim();
  return trimmed.length > 0 && !GENERIC_ERROR_NAMES.has(trimmed);
}

/**
 * Reads a policy or server message from ZenStack client fetch errors when `Error.message` is generic.
 */
function zenStackClientDetailMessage(error: ErrorLike): string | undefined {
  const nested = error.info?.error?.message ?? error.info?.message;
  if (typeof nested !== 'string') {
    return undefined;
  }
  const trimmed = nested.trim();
  return isInformativeMessage(trimmed) ? trimmed : undefined;
}

/**
 * Shortens HTTP response bodies for display in error toasts.
 */
function summarizeResponseBody(body: string): string {
  const trimmed = body.trim();
  if (!trimmed) {
    return '';
  }

  try {
    const parsed = JSON.parse(trimmed) as {
      error?: { message?: string };
      message?: string;
    };
    const nested = parsed.error?.message ?? parsed.message;
    if (typeof nested === 'string' && nested.trim()) {
      return nested.trim();
    }
  } catch {
    // fall through to raw body
  }

  return trimmed.length > 240 ? `${trimmed.slice(0, 240)}…` : trimmed;
}

/**
 * Extracts a message from AI SDK / provider-shaped error objects.
 */
function extractStructuredErrorMessage(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }

  const structured = error as StructuredErrorLike;
  const parts: string[] = [];

  if (typeof structured.message === 'string' && isInformativeMessage(structured.message)) {
    parts.push(structured.message.trim());
  }

  if (typeof structured.statusCode === 'number') {
    parts.push(`HTTP ${structured.statusCode}`);
  }

  if (typeof structured.responseBody === 'string') {
    const summary = summarizeResponseBody(structured.responseBody);
    if (summary) {
      parts.push(summary);
    }
  }

  if (typeof structured.modelId === 'string') {
    const modelType = typeof structured.modelType === 'string' ? `${structured.modelType} ` : '';
    parts.push(`Model not found: ${modelType}${structured.modelId}`);
  }

  if (typeof structured.text === 'string' && structured.text.trim()) {
    parts.push(`Invalid JSON response: ${structured.text.trim().slice(0, 120)}`);
  }

  if (structured.context && typeof structured.context === 'object') {
    const field =
      structured.context.field ?? structured.context.entityName ?? structured.context.entityId;
    if (field) {
      parts.push(`Invalid AI response field: ${field}`);
    }
  }

  if (parts.length === 0 && structured.data !== undefined) {
    try {
      const serialized = JSON.stringify(structured.data);
      if (serialized && serialized !== '{}') {
        parts.push(serialized.length > 240 ? `${serialized.slice(0, 240)}…` : serialized);
      }
    } catch {
      // ignore non-serializable data
    }
  }

  if (parts.length === 0) {
    return undefined;
  }

  return parts.join(' — ');
}

/**
 * Resolves a human-readable message from an unknown error value.
 */
export function getErrorMessage(error: unknown): string {
  if (!error) {
    return UNKNOWN_ERROR_MESSAGE;
  }

  if (typeof error === 'string') {
    const trimmed = error.trim();
    return trimmed ? sanitizeMessage(trimmed) : UNKNOWN_ERROR_MESSAGE;
  }

  if (error instanceof Error) {
    const primary = error.message.trim();
    if (primary === ZENSTACK_GENERIC_FETCH_ERROR_MESSAGE) {
      const fromInfo = zenStackClientDetailMessage(error as ErrorLike);
      if (fromInfo) {
        return sanitizeMessage(fromInfo);
      }
    }
    if (isInformativeMessage(primary)) {
      return sanitizeMessage(primary);
    }

    const structured = extractStructuredErrorMessage(error);
    if (structured) {
      return sanitizeMessage(structured);
    }

    if (error.cause !== undefined) {
      const causeMessage = getErrorMessage(error.cause);
      if (causeMessage !== UNKNOWN_ERROR_MESSAGE) {
        return causeMessage;
      }
    }

    return UNKNOWN_ERROR_MESSAGE;
  }

  const errorLike = error as ErrorLike;

  // `meta.reason`/`info.reason` are the ZenStack 2 shapes; `reason` is the ZenStack 3 `ORMError`
  // field, which survives JSON serialization as an own enumerable property.
  if (
    errorLike.meta?.reason === 'ACCESS_POLICY_VIOLATION' ||
    errorLike.info?.reason === 'ACCESS_POLICY_VIOLATION' ||
    errorLike.reason === 'rejected-by-policy'
  ) {
    return 'You are not authorized to perform this action.';
  }

  const structured = extractStructuredErrorMessage(error);
  if (structured) {
    return sanitizeMessage(structured);
  }

  let message = UNKNOWN_ERROR_MESSAGE;
  if (errorLike.meta?.reason || errorLike.info?.reason || errorLike.reason) {
    message = errorLike.message?.trim() || UNKNOWN_ERROR_MESSAGE;
  } else if (errorLike.info?.message) {
    message = errorLike.info.message;
  } else if (errorLike.message?.trim()) {
    message = errorLike.message.trim();
  } else if (errorLike.name && !GENERIC_ERROR_NAMES.has(errorLike.name)) {
    message = errorLike.name;
  }

  if (!isInformativeMessage(message) && errorLike.cause !== undefined) {
    const causeMessage = getErrorMessage(errorLike.cause);
    if (causeMessage !== UNKNOWN_ERROR_MESSAGE) {
      message = causeMessage;
    }
  }

  return sanitizeMessage(message);
}

/**
 * Returns a user-facing error message, substituting a fallback when extraction fails.
 */
export function getDisplayErrorMessage(
  error: unknown,
  fallback = CHAT_STREAM_FALLBACK_MESSAGE,
): string {
  const message = getErrorMessage(error);
  if (message === UNKNOWN_ERROR_MESSAGE || message === HTTP_FETCH_FAILURE_MESSAGE) {
    return fallback;
  }
  return message;
}
